import {
  NodeViewRendererProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  ReactRenderer,
} from '@tiptap/react';

import {
  type MentionOptions,
  mentionExtension,
} from '@copera/tiptap/extensions';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { ListItemAvatar, ListItemText, MenuItem } from '@mui/material';
import { SuggestionProps } from '@tiptap/suggestion';
import classNames from 'classnames';
import type { IWorkspaceUser } from 'src/@types/workspace-info';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { CustomMenu } from '../private/components/CustomMenu';
import { UserMentionItem } from '../private/types/EditorExtensionsConfig';
import { getExtensionOption } from '../private/utils/getExtensionOption';
import { getExternalStore } from '../private/utils/getExternalStores';

const MentionList = ({
  items,
  command,
  clientRect,
  editor,
  onClose,
}: SuggestionProps<UserMentionItem> & { onClose(): void }) => {
  const selectItem = (index: number) => {
    const item = items[index];

    if (item) {
      command({ id: item._id } as any);
    }
  };

  if (!items?.length) {
    return null;
  }

  return (
    <CustomMenu
      {...{ clientRect, editor, onClose, selectItem, totalItems: items.length }}
    >
      {({ selectedIndex }) =>
        items.map((item, index) => (
          <MenuItem
            dense
            key={item._id}
            onClick={() => selectItem(index)}
            selected={selectedIndex === index}
          >
            <ListItemAvatar>
              <CPAvatar
                name={item.name}
                picture={item.picture}
                status={item.status}
                size="small"
              />
            </ListItemAvatar>
            <ListItemText primary={item.name} />
          </MenuItem>
        ))
      }
    </CustomMenu>
  );
};

const UserMentionPopoverComponent = ({
  node,
  extension,
  editor,
}: NodeViewRendererProps) => {
  if (!node?.attrs.id) return <>@mention</>;

  const isCurrentUser =
    getExtensionOption(editor, 'userMention').currentUserId === node.attrs.id;

  return (
    <NodeViewWrapper as="span">
      <UserProfileShortPopoverTrigger userId={node.attrs.id}>
        <span
          className={classNames('user-mention', { '--me': isCurrentUser })}
          contentEditable={false}
        >
          {extension.options.renderLabel.call(
            { editor },
            { node, options: extension.options },
          )}
        </span>
      </UserProfileShortPopoverTrigger>
    </NodeViewWrapper>
  );
};

const MentionExtended = mentionExtension.extend<MentionOptions>({
  atom: true,
  addNodeView() {
    return ReactNodeViewRenderer(UserMentionPopoverComponent);
  },
});

export const userMentionExtension = MentionExtended.configure({
  entityType: 'user',
  renderLabel({ node }) {
    const workspaceInfo = getExternalStore(this.editor, 'workspaceStore');
    if (!workspaceInfo) return `@${node.attrs.label || 'mention'}`;

    const user = workspaceInfo.getState().usersById[node.attrs.id];
    if (!user) return '@mention';
    return `@${user.name.trim()}`;
  },
  suggestion: {
    char: '@',
    items(props) {
      const searchUsers = (list: IWorkspaceUser[]) => {
        return list.filter((user) =>
          user.name.toLowerCase().includes(props.query.toLowerCase()),
        );
      };

      const userMention = getExtensionOption(props.editor, 'userMention');

      if (userMention.mentionableUsers?.length)
        return searchUsers(userMention.mentionableUsers);

      const workspaceInfo = getExternalStore(props.editor, 'workspaceStore');
      if (!workspaceInfo) return [];

      return searchUsers(workspaceInfo.getState().activeUsers);
    },
    render: () => {
      let component: ReactRenderer;

      const onClose = () => {
        component?.destroy();
      };

      return {
        onStart(props) {
          component = new ReactRenderer(MentionList, {
            props: { onClose, ...props },
            editor: props.editor,
          });
        },
        onUpdate(props) {
          component.updateProps(props);
        },
        onExit() {
          onClose();
        },
      };
    },
  },
});
